import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Receipt, Users, Package, Trophy, Building2, Shield, HelpCircle,
  Moon, Sun, LogOut, ChevronRight, X, MapPin, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useAppRefresh } from '../hooks/useAppRefresh';
import { MODULES } from '../constants/modules';

const getInitials = (nameOrUsername) => {
  const raw = (nameOrUsername || '').trim();
  if (!raw) return 'GA';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

/**
 * MoreSheet — the "More" bottom sheet for mobile navigation (issue #50).
 * Surfaces everything not on the bottom tab bar: a profile row, a module grid,
 * a dark-mode toggle and logout. Module tiles respect the user's permissions.
 */
export default function MoreSheet({ open, onClose }) {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { refreshApp, refreshing } = useAppRefresh();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    // Lock background scroll while the sheet is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const profileName = user?.name || user?.username || 'User';
  const profileRole = user?.role_name || 'Admin';
  const initials = getInitials(profileName);
  const profilePath = isSuperAdmin ? '/superadmin/settings' : '/dashboard/profile';

  const can = (mod, access) => isSuperAdmin || hasPermission(mod, access);

  const tiles = [
    { label: 'Reports',       to: '/dashboard/reports',                icon: FileText,  show: can(MODULES.REPORTS) },
    { label: 'Expenses',      to: '/dashboard/expenses',               icon: Receipt,   show: can(MODULES.EXPENSES) },
    { label: 'Staff',         to: '/dashboard/staff',                  icon: Users,     show: can(MODULES.STAFF) },
    { label: 'Assets',        to: '/dashboard/assets',                 icon: Package,   show: can(MODULES.EQUIPMENT) },
    { label: 'Tournaments',   to: '/dashboard/tournaments',            icon: Trophy,    show: can(MODULES.TOURNAMENTS) },
    { label: 'Configure',     to: '/dashboard/facility-services',      icon: Building2, show: can(MODULES.CONFIGURATION, 'write') },
    { label: 'Ground location', to: '/dashboard/facility-location',    icon: MapPin,    show: can(MODULES.CONFIGURATION, 'write') },
    { label: 'Users & roles', to: '/dashboard/roles',                  icon: Shield,    show: can(MODULES.ROLES) || can(MODULES.USERS) },
    { label: 'Help',          to: '/dashboard/help-center',            icon: HelpCircle, show: can(MODULES.HELP_CENTER) },
  ].filter((t) => t.show);

  const go = (to) => { onClose?.(); navigate(to); };

  return (
    <div className="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="More menu">
      <button
        type="button"
        aria-label="Close menu"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-ga-border-subtle bg-ga-bg-surface pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-ga-elevated animate-[ga-sheet-up_0.22s_ease-out]">
        <div className="sticky top-0 flex items-center justify-between bg-ga-bg-surface px-4 pt-3 pb-2">
          <span className="mx-auto h-1 w-10 rounded-full bg-ga-border-subtle" aria-hidden />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-2 rounded-full p-1.5 text-ga-text-muted hover:text-ga-text-primary"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* Profile row */}
        <button
          type="button"
          onClick={() => go(profilePath)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ga-primary text-sm font-bold text-ga-text-on-primary">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-ga-text-primary">{profileName}</div>
            <div className="truncate text-[12px] text-ga-text-muted">{profileRole}</div>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-ga-text-subtle" aria-hidden />
        </button>

        <div className="mx-4 my-1 border-t border-ga-border-subtle" />

        {/* Module grid */}
        {tiles.length > 0 && (
          <div className="grid grid-cols-4 gap-1 px-3 py-3">
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.to}
                  type="button"
                  onClick={() => go(t.to)}
                  className="flex flex-col items-center gap-1.5 rounded-ga-md px-1 py-3 text-center transition-colors hover:bg-ga-bg-surface-subtle/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ga-focus"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-ga-md bg-ga-primary-soft text-ga-primary">
                    <Icon className="h-[22px] w-[22px]" aria-hidden />
                  </span>
                  <span className="text-[11px] font-medium leading-tight text-ga-text-primary">{t.label}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mx-4 my-1 border-t border-ga-border-subtle" />

        {/* Refresh app data */}
        <button
          type="button"
          onClick={() => { refreshApp(); }}
          disabled={refreshing}
          className="flex w-full items-center gap-3 px-4 py-3 text-left disabled:opacity-60"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ga-md bg-ga-bg-surface-subtle/60 text-ga-text-muted">
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} aria-hidden />
          </span>
          <span className="flex-1 text-sm font-medium text-ga-text-primary">
            {refreshing ? 'Refreshing…' : 'Refresh app'}
          </span>
        </button>

        <div className="mx-4 my-1 border-t border-ga-border-subtle" />

        {/* Dark-mode toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          className="flex w-full items-center gap-3 px-4 py-3 text-left"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ga-md bg-ga-bg-surface-subtle/60 text-ga-text-muted">
            {theme === 'light' ? <Moon className="h-5 w-5" aria-hidden /> : <Sun className="h-5 w-5" aria-hidden />}
          </span>
          <span className="flex-1 text-sm font-medium text-ga-text-primary">
            {theme === 'light' ? 'Dark mode' : 'Light mode'}
          </span>
          <span className="text-[12px] text-ga-text-subtle">{theme === 'light' ? 'Off' : 'On'}</span>
        </button>

        {/* Logout */}
        <button
          type="button"
          onClick={() => { onClose?.(); logout(); navigate('/login'); }}
          className="flex w-full items-center gap-3 px-4 py-3 text-left text-ga-danger"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-ga-md bg-ga-danger/10">
            <LogOut className="h-5 w-5" aria-hidden />
          </span>
          <span className="flex-1 text-sm font-semibold">Logout</span>
        </button>
      </div>
    </div>
  );
}
